import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { AuthB2CService } from 'common-web-core';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { userDataMock } from 'src/mocks/data/user-data-mock';

import { ResetUserAction, StoreUserAction } from '../../states/user/user-state.actions';
import { User } from '../../states/user/user-state.model';
import { UserState } from '../../states/user/user.state';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  user$: Observable<User>;

  constructor(private store: Store, private authB2cService: AuthB2CService) {
    this.user$ = this.store.select(UserState.getUser);
  }

  public getUser(): Observable<User> {
    const userSnapshot = this.store.selectSnapshot(UserState.getUserState);

    if (userSnapshot.isLoadedSuccessfully) {
      if (environment.mock) {
        return of(userDataMock);
      } else {
        return of(userSnapshot.user);
      }
    }

    return this.authB2cService.loggedIn$.pipe(
      filter((isLoggedIn) => !!isLoggedIn),
      mergeMap(() => {
        return forkJoin({
          userId: this.authB2cService.tryGetClaim('userId'),
          b2cId: this.authB2cService.tryGetClaim('sub'),
          salesForceMasterContactId: this.authB2cService.tryGetClaim(
            'salesForceMasterContactId'
          ),
          name: this.authB2cService.tryGetClaim('name'),
          givenName: this.authB2cService.tryGetClaim('given_name'),
          lastName: this.authB2cService.tryGetClaim('family_name'),
          email: this.authB2cService.tryGetClaim('email')
        });
      }),
      map((user) => {
        if (!!user && !!user.email) return user as User;

        throw new Error('User information could not be fetched.');
      }),
      tap((user) => {
        const storeUserAction = new StoreUserAction(user, true);
        this.store.dispatch(storeUserAction);
      })
    );
  }

  @Dispatch()
  public resetState(): ResetUserAction {
    return new ResetUserAction();
  }
}
